import * as XLSX from "xlsx";
import {
  getColumnWidths,
  getFeedbacksSheet,
  calculateStatistics,
  createSequenceStatisticsSheet,
  createInfoSheet,
} from "./common";
import * as _ from "lodash";
import { TInstancesMC, TFormattedResults } from "types/multipleCriteria";
import { TMultipleChannelSession } from "types/multipleChannel";

export const exportMultipleChannelResults = (
  session: TMultipleChannelSession,
  instances: TInstancesMC
) => {
  const excelFile = XLSX.utils.book_new();

  // Créer l'onglet d'informations générales
  const infoSheet = createInfoSheet(
    session.metadata.name,
    "Multiple Channel",
    session.metadata.context
  );
  XLSX.utils.book_append_sheet(excelFile, infoSheet, "Information");

  const feedbacksSheet = getFeedbacksSheet(session, instances);
  const { formatedResults, channelStats } = getStatsData(session, instances);

  // Grouper les résultats par séquence
  const resultsBySequence = _.groupBy(formatedResults, "sequenceId");

  // Créer un onglet pour chaque séquence
  Object.keys(resultsBySequence).forEach((sequenceId, index) => {
    const sequenceResults = resultsBySequence[sequenceId];
    const resultsSheet = createResultsSheet(sequenceResults);

    if (resultsSheet) {
      XLSX.utils.book_append_sheet(
        excelFile,
        resultsSheet,
        `Sequence ${index + 1}`
      );
    }

    // Créer un onglet de statistiques pour cette séquence
    const sequenceStats = getSequenceStatistics(session, instances, sequenceId);
    const sequenceStatsSheet = createSequenceStatisticsSheetWithAttributes(
      sequenceStats,
      session,
      ["Image", "Average", "Median", "Standard deviation"]
    );

    if (sequenceStatsSheet) {
      XLSX.utils.book_append_sheet(
        excelFile,
        sequenceStatsSheet,
        `Stats Sequence ${index + 1}`
      );
    }
  });

  // Créer l'onglet Statistiques globales
  const statisticsSheet = createStatisticsSheet(channelStats, session);
  if (statisticsSheet) {
    XLSX.utils.book_append_sheet(excelFile, statisticsSheet, "Statistics");
  }

  if (feedbacksSheet) {
    XLSX.utils.book_append_sheet(excelFile, feedbacksSheet, "Feedbacks");
  }

  XLSX.writeFile(excelFile, `export_session_${session.id}.xlsx`);
};

const createResultsSheet = (resutsPerSequences: TFormattedResults) => {
  if (!resutsPerSequences || resutsPerSequences.length === 0) {
    return null;
  }

  const excelRows = [];
  const allHeaders = [...Object.keys(resutsPerSequences[0] || {})];
  // Exclure sequenceId de l'affichage
  const headers = allHeaders.filter((header) => header !== "sequenceId");
  excelRows.push(headers);

  resutsPerSequences.forEach((row: any) => {
    const rowData = headers.map((header) => row[header] ?? "");
    excelRows.push(rowData);
  });

  const resultsSheet = XLSX.utils.aoa_to_sheet(excelRows);
  const colWidths = getColumnWidths(resutsPerSequences);
  resultsSheet["!cols"] = colWidths;

  return resultsSheet;
};

const getStatsData = (
  session: TMultipleChannelSession,
  instances: TInstancesMC
) => {
  const { data, metadata } = session;
  const { mcCriterias = [] } = data;
  const { personalQuestions = [] } = metadata;
  const results = {};

  instances.map((instance) => _.merge(results, instance.results));

  const formatedResults: any[] = [];
  const channelStats: any[] = [];

  // Initialiser les statistiques pour chaque canal non-fixed
  mcCriterias.forEach((channel) => {
    // Ne prendre que les canaux non-fixed (avec des valeurs saisies)
    if (!channel.isFixed) {
      channelStats.push({
        channelId: channel.id,
        imageName: channel.name, // Le nom de l'image
        values: [],
      });
    }
  });

  _.keys(results).map((sequenceId) => {
    // @ts-ignore
    const sequenceResult = results[sequenceId];

    const participantsVote = _.omit(sequenceResult, "_fakeId");

    for (let participantId in participantsVote) {
      // Récupérer le nom du participant
      const participant = instances
        ?.find((instance) =>
          instance.participants?.find((p) => p.id === participantId)
        )
        ?.participants?.find((p) => p.id === participantId);

      const participantName =
        participant?.infos &&
        typeof participant.infos === "object" &&
        "name" in participant.infos
          ? (participant.infos as any).name
          : participantId;

      const sequenceFormatedResult: any = {
        sequenceId: sequenceId,
        Participant: participantName,
      };
      const vote = participantsVote[participantId];

      mcCriterias.forEach((channel) => {
        if (!channel.isFixed) {
          // @ts-ignore
          sequenceFormatedResult[channel.name] = null;
        }
      });

      for (let channelId in vote) {
        // @ts-ignore
        const channel = mcCriterias.find((c) => c.id === channelId);

        if (channel) {
          // @ts-ignore
          sequenceFormatedResult[channel.name] = vote[channelId];

          // Collecter les valeurs pour les statistiques (canaux non-fixed uniquement)
          channelStats.forEach((stat) => {
            if (stat.channelId === channelId) {
              const value = parseFloat(vote[channelId]);
              // Vérifier que c'est bien un nombre valide
              if (!isNaN(value)) {
                stat.values.push(value);
              }
            }
          });
        }
      }

      // Add personal questions columns
      personalQuestions.forEach((question) => {
        const instance = instances.find(
          (inst) => inst.participants?.[0]?.id === participantId
        );

        const answer = instance?.personalQuestions?.[question.id] || "";
        sequenceFormatedResult[question.question] = answer;
      });

      formatedResults.push(sequenceFormatedResult);
    }

    return null;
  });

  // Calculer les statistiques finales
  const finalStats = channelStats.map((stat) => {
    const stats = calculateStatistics(stat.values);
    return {
      imageName: stat.imageName,
      ...stats,
    };
  });

  return { formatedResults, channelStats: finalStats };
};

const createStatisticsSheet = (
  channelStats: any[],
  session: TMultipleChannelSession
) => {
  if (!channelStats || channelStats.length === 0) {
    return null;
  }

  const excelRows = [];

  // En-têtes de base
  const baseHeaders = ["Image", "Average", "Median", "Standard deviation"];

  // Ajouter les colonnes d'attributs
  const attributeHeaders =
    session.metadata.attributes?.map((attr) => `${attr.name} - ${attr.unit}`) ||
    [];
  const allHeaders = [...baseHeaders, ...attributeHeaders];

  excelRows.push(allHeaders);

  // Données
  channelStats.forEach((stat) => {
    const baseData = [
      stat.imageName,
      stat.average,
      stat.median,
      stat.standardDeviation,
    ];

    // Ajouter les valeurs des attributs
    const attributeValues =
      session.metadata.attributes?.map((attr, index) => {
        const resource = session.data.mcCriterias.find(
          (r) => r.name === stat.imageName
        );
        return resource?.attributes?.[index]?.quantity || "";
      }) || [];

    const allData = [...baseData, ...attributeValues];
    excelRows.push(allData);
  });

  const statisticsSheet = XLSX.utils.aoa_to_sheet(excelRows);

  // Ajuster la largeur des colonnes
  const colWidths = [
    { width: 25 }, // Image
    { width: 15 }, // Average
    { width: 15 }, // Median
    { width: 15 }, // Standard deviation
  ];

  // Ajouter les largeurs pour les colonnes d'attributs
  attributeHeaders.forEach(() => {
    colWidths.push({ width: 20 });
  });

  statisticsSheet["!cols"] = colWidths;

  return statisticsSheet;
};

// Fonction pour calculer les statistiques d'une séquence spécifique
const getSequenceStatistics = (
  session: TMultipleChannelSession,
  instances: TInstancesMC,
  sequenceId: string
) => {
  const { data } = session;
  const { mcCriterias = [] } = data;
  const results = {};
  const sequenceStats: any[] = [];

  instances.map((instance) => _.merge(results, instance.results));

  // Initialiser les statistiques pour cette séquence
  mcCriterias.forEach((channel) => {
    // Ne prendre que les canaux non-fixed (avec des valeurs saisies)
    if (!channel.isFixed) {
      sequenceStats.push({
        channelId: channel.id,
        imageName: channel.name, // Le nom de l'image
        values: [],
      });
    }
  });

  // Récupérer les résultats pour cette séquence spécifique
  // @ts-ignore
  const sequenceResult = results[sequenceId];
  if (!sequenceResult) {
    return [];
  }

  const participantsVote = _.omit(sequenceResult, "_fakeId");

  // Collecter les valeurs pour cette séquence
  for (let participantId in participantsVote) {
    const vote = participantsVote[participantId];

    for (let channelId in vote) {
      // @ts-ignore
      const channel = mcCriterias.find((c) => c.id === channelId);

      if (channel && !channel.isFixed) {
        sequenceStats.forEach((stat) => {
          if (stat.channelId === channelId) {
            const value = parseFloat(vote[channelId]);
            // Vérifier que c'est bien un nombre valide
            if (!isNaN(value)) {
              stat.values.push(value);
            }
          }
        });
      }
    }
  }

  // Calculer les statistiques finales pour cette séquence
  const finalStats = sequenceStats.map((stat) => {
    const stats = calculateStatistics(stat.values);
    return {
      imageName: stat.imageName,
      ...stats,
    };
  });

  return finalStats;
};

// Fonction pour créer un onglet de statistiques de séquence avec attributs
const createSequenceStatisticsSheetWithAttributes = (
  sequenceStats: any[],
  session: TMultipleChannelSession,
  baseHeaders: string[]
) => {
  if (!sequenceStats || sequenceStats.length === 0) {
    return null;
  }

  const excelRows = [];

  // En-têtes de base
  const attributeHeaders =
    session.metadata.attributes?.map((attr) => `${attr.name} - ${attr.unit}`) ||
    [];
  const allHeaders = [...baseHeaders, ...attributeHeaders];

  excelRows.push(allHeaders);

  // Données
  sequenceStats.forEach((stat) => {
    const baseData = baseHeaders.map((header) => {
      switch (header) {
        case "Average":
          return stat.average;
        case "Median":
          return stat.median;
        case "Standard deviation":
          return stat.standardDeviation;
        default:
          return stat.imageName || stat.resourceName || "";
      }
    });

    // Ajouter les valeurs des attributs
    const attributeValues =
      session.metadata.attributes?.map((attr, index) => {
        const resource = session.data.mcCriterias.find(
          (r) => r.name === stat.imageName
        );
        return resource?.attributes?.[index]?.quantity || "";
      }) || [];

    const allData = [...baseData, ...attributeValues];
    excelRows.push(allData);
  });

  const statisticsSheet = XLSX.utils.aoa_to_sheet(excelRows);

  // Ajuster la largeur des colonnes
  const colWidths = allHeaders.map(() => ({ width: 20 }));
  statisticsSheet["!cols"] = colWidths;

  return statisticsSheet;
};
